import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {mbeConfigService} from './mbe-config.service';
import {Observable} from 'rxjs';
import {BasicResponse} from '@auth-suite-services/services';
import {FraudRuleList, FraudRuleListAPI} from '../model/fraud-rule-list';

/** See C001 - 2026-02-02 - HE: Backend %JSONImport() requires exact types; "" for numeric/boolean causes ERROR #9406 */
function normalizeFrlPayload(raw: Record<string, unknown>, isUpdate: boolean): Record<string, unknown> {
  const n = (v: unknown) => (v === '' || v === null || v === undefined ? 0 : Number(v));
  const s = (v: unknown) => (v == null ? '' : String(v));
  const toDateStr = (v: unknown): string | null => {
    if (v == null || v === '') return null;
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    return typeof v === 'object' ? null : String(v);
  };
  const payload: Record<string, unknown> = {
    cardCategory: n(raw['cardCategory']) || 1,
    description: s(raw['description']),
    EVEID: n(raw['EVEID']),
    FRLGid: n(raw['FRLGid']),
    fraudActionType: n(raw['fraudActionType']) || 1,
    fraudRuleActivated: Boolean(raw['fraudRuleActivated']),
    frontEndUserName: s(raw['frontEndUserName']),
    largeAmount: n(raw['largeAmount']),
    maxConsecDaysMaxLimit: n(raw['maxConsecDaysMaxLimit']),
    maxDeclinePerDay: n(raw['maxDeclinePerDay']),
    maxTransPHourSameMerchant: n(raw['maxTransPHourSameMerchant']),
    name: s(raw['name']),
    OBJVersion: n(raw['OBJVersion']),
    smallAmount: n(raw['smallAmount']),
    UACID: s(raw['UACID']),
    USEID: s(raw['USEID'])
  };
  // See C002 - 2026-02-03 - HE: Backend VALUELIST rejects 0; omit when 0
  const fd = n(raw['fraudDataset']);
  if (fd === 1 || fd === 2) payload['fraudDataset'] = fd;
  const tov = n(raw['typeOfValue']);
  if (tov === 1 || tov === 2 || tov === 3) payload['typeOfValue'] = tov;
  const tovol = n(raw['typeOfVolume']);
  if (tovol >= 1 && tovol <= 6) payload['typeOfVolume'] = tovol;
  const rd = toDateStr(raw['recordDate']);
  const rt = raw['recordTime'];
  if (rd) payload['recordDate'] = rd;
  if (rt != null && rt !== '') payload['recordTime'] = s(rt);
  const idVal = raw['id'];
  if (isUpdate && idVal != null && idVal !== '') payload['id'] = String(idVal);
  return payload;
}

@Injectable({
  providedIn: 'root'
})
export class FraudRuleListService {

  private URL;
  headers: HttpHeaders;

  constructor(@Inject(mbeConfigService) private config, private httpClient: HttpClient) {
    this.URL = config.REST_SERVER_HOST + config.REST_SERVER_url.find(restUrl => restUrl.api === 'MBE').url;
    this.headers = new HttpHeaders().set('Content-Type', 'application/json');
  }

  getAllFRL(pageIndex: number, pageSize: number, sortActive: string, sort: string): Observable<FraudRuleListAPI> {
    console.log('PageIndex; PageSize; SortActive; Sort');
    console.log(pageIndex, pageSize, sortActive, sort);
    const headers = this.headers;
    // tslint:disable-next-line:max-line-length
    return this.httpClient.get<FraudRuleListAPI>(`${this.URL}/frl/getallfrl/${pageIndex}/${pageSize}/${sortActive ? sortActive : 'id'}/${sort ? sort : 'asc'}`, {headers});
  }

  getFRL(id: number) {
    console.log('ID');
    console.log(id);
    const headers = this.headers;
    return this.httpClient.get<FraudRuleList>(`${this.URL}/frl/getfrl/${id}`, {headers});
  }

  updateFRL(frl: FraudRuleList | Record<string, unknown>): Observable<BasicResponse> {
    const headers = this.headers;
    const payload = normalizeFrlPayload(frl as Record<string, unknown>, true);
    console.log('Update FRL: ' + JSON.stringify(payload));
    return this.httpClient.post<BasicResponse>(`${this.URL}/frl/updatefrl`, payload, { headers });
  }

  // Added new method to create a new FRL. 03/10/2025 PK.
  createFRL(frl: FraudRuleList | Record<string, unknown>): Observable<BasicResponse> {
    const headers = this.headers;
    const payload = normalizeFrlPayload(frl as Record<string, unknown>, false);
    return this.httpClient.post<BasicResponse>(`${this.URL}/frl/createfrl`, payload, { headers });
  }
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Backend %JSONImport() strict; ERROR #9406 when numeric/boolean sent as "". What changed: Added normalizeFrlPayload() to coerce form values before POST. All numeric fields (EVEID, FRLGid, fraudDataset, typeOfValue, typeOfVolume, largeAmount, smallAmount, maxConsecDaysMaxLimit, maxDeclinePerDay, maxTransPHourSameMerchant, OBJVersion, cardCategory, fraudActionType) use Number() with 0 fallback. fraudRuleActivated uses Boolean(). recordDate/recordTime omitted when empty (empty string may fail). id: string for update only. Previous behavior: Form getRawValue() returns "" for empty inputs; backend rejects. New behavior: Payload always has correct JSON types. Reason: FraudRuleList API Payload Guide 02/02/2026. Impact: createfrl/updatefrl requests succeed. - 2026-02-02 - HE
// C002: ERROR #7205 VALUELIST rejects 0 for fraudDataset, typeOfValue, typeOfVolume. What changed: Omit these fields when value is 0; only include when in valid range (fraudDataset: 1-2, typeOfValue: 1-3, typeOfVolume: 1-6). Previous behavior: Sending 0 caused "Datatype value '0' not in VALUELIST". New behavior: Fields omitted when empty/0; backend accepts. Impact: createFRL succeeds when user leaves these optional. - 2026-02-03 - HE
